import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import apiClient from '../../api/client';
import administrationApi, { Country } from '../../api/administrationApi';
import metricsApi, { Metric } from '../../api/metricsApi';
import { theme } from '../../styles/theme';
import MultiColumnSelect from '../shared/MultiColumnSelect';

interface CollectionFormProps {
  onSubmitSuccess: (count: number) => void;
  onSubmitError: (error: string) => void;
}

const CollectionForm: React.FC<CollectionFormProps> = ({ onSubmitSuccess, onSubmitError }) => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedMetrics, setSelectedMetrics] = useState<number[]>([]);
  const [collectionYear, setCollectionYear] = useState<number>(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  // Fetch countries and metrics on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        // Fetch countries from administration service
        const [countriesResponse, metricsResponse] = await Promise.all([
          administrationApi.getCountries({ limit: 100, page: 1, is_active: true }),
          metricsApi.getMetrics({ limit: 100, page: 1 })
        ]);

        if (countriesResponse?.countries) {
          setCountries(countriesResponse.countries);
        }

        if (metricsResponse) {
          setMetrics(metricsResponse);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        // Use fallback data if API fails
        const fallbackCountries: Country[] = [
          // Americas
          { id: 1, iso_code: 'US', name: 'United States', brave_country_code: 'US', region: 'Americas', currency_code: 'USD', flag_emoji: '🇺🇸', display_order: 1, is_active: true },
          { id: 2, iso_code: 'CA', name: 'Canada', brave_country_code: 'CA', region: 'Americas', currency_code: 'CAD', flag_emoji: '🇨🇦', display_order: 2, is_active: true },
          { id: 3, iso_code: 'MX', name: 'Mexico', brave_country_code: 'MX', region: 'Americas', currency_code: 'MXN', flag_emoji: '🇲🇽', display_order: 3, is_active: true },
          { id: 4, iso_code: 'BR', name: 'Brazil', brave_country_code: 'BR', region: 'Americas', currency_code: 'BRL', flag_emoji: '🇧🇷', display_order: 4, is_active: true },
          { id: 5, iso_code: 'AR', name: 'Argentina', brave_country_code: 'AR', region: 'Americas', currency_code: 'ARS', flag_emoji: '🇦🇷', display_order: 5, is_active: true },

          // Europe
          { id: 6, iso_code: 'GB', name: 'United Kingdom', brave_country_code: 'GB', region: 'Europe', currency_code: 'GBP', flag_emoji: '🇬🇧', display_order: 6, is_active: true },
          { id: 7, iso_code: 'DE', name: 'Germany', brave_country_code: 'DE', region: 'Europe', currency_code: 'EUR', flag_emoji: '🇩🇪', display_order: 7, is_active: true },
          { id: 8, iso_code: 'FR', name: 'France', brave_country_code: 'FR', region: 'Europe', currency_code: 'EUR', flag_emoji: '🇫🇷', display_order: 8, is_active: true },
          { id: 9, iso_code: 'IT', name: 'Italy', brave_country_code: 'IT', region: 'Europe', currency_code: 'EUR', flag_emoji: '🇮🇹', display_order: 9, is_active: true },
          { id: 10, iso_code: 'ES', name: 'Spain', brave_country_code: 'ES', region: 'Europe', currency_code: 'EUR', flag_emoji: '🇪🇸', display_order: 10, is_active: true },

          // Asia
          { id: 11, iso_code: 'JP', name: 'Japan', brave_country_code: 'JP', region: 'Asia', currency_code: 'JPY', flag_emoji: '🇯🇵', display_order: 11, is_active: true },
          { id: 12, iso_code: 'CN', name: 'China', brave_country_code: 'CN', region: 'Asia', currency_code: 'CNY', flag_emoji: '🇨🇳', display_order: 12, is_active: true },
          { id: 13, iso_code: 'IN', name: 'India', brave_country_code: 'IN', region: 'Asia', currency_code: 'INR', flag_emoji: '🇮🇳', display_order: 13, is_active: true },
          { id: 14, iso_code: 'KR', name: 'South Korea', brave_country_code: 'KR', region: 'Asia', currency_code: 'KRW', flag_emoji: '🇰🇷', display_order: 14, is_active: true },
          { id: 15, iso_code: 'SG', name: 'Singapore', brave_country_code: 'SG', region: 'Asia', currency_code: 'SGD', flag_emoji: '🇸🇬', display_order: 15, is_active: true },

          // Oceania
          { id: 16, iso_code: 'AU', name: 'Australia', brave_country_code: 'AU', region: 'Oceania', currency_code: 'AUD', flag_emoji: '🇦🇺', display_order: 16, is_active: true },
          { id: 17, iso_code: 'NZ', name: 'New Zealand', brave_country_code: 'NZ', region: 'Oceania', currency_code: 'NZD', flag_emoji: '🇳🇿', display_order: 17, is_active: true },

          // Africa
          { id: 18, iso_code: 'ZA', name: 'South Africa', brave_country_code: 'ZA', region: 'Africa', currency_code: 'ZAR', flag_emoji: '🇿🇦', display_order: 18, is_active: true },
          { id: 19, iso_code: 'NG', name: 'Nigeria', brave_country_code: 'NG', region: 'Africa', currency_code: 'NGN', flag_emoji: '🇳🇬', display_order: 19, is_active: true },
          { id: 20, iso_code: 'EG', name: 'Egypt', brave_country_code: 'EG', region: 'Africa', currency_code: 'EGP', flag_emoji: '🇪🇬', display_order: 20, is_active: true },
        ];

        const fallbackMetrics: Metric[] = [
          // Aggregatable metrics
          { id: 1, metricId: 'GDP', name: 'GDP', definition: 'Gross Domestic Product', unit: 'USD', isAggregatable: true },
          { id: 2, metricId: 'GDP_PC', name: 'GDP Per Capita', definition: 'GDP per person', unit: 'USD', isAggregatable: true },
          { id: 3, metricId: 'POP', name: 'Population', definition: 'Total Population', unit: 'People', isAggregatable: true },
          { id: 4, metricId: 'TRADE', name: 'Trade Balance', definition: 'Export minus Import', unit: 'USD', isAggregatable: true },
          { id: 5, metricId: 'FDI', name: 'Foreign Direct Investment', definition: 'Net FDI inflows', unit: 'USD', isAggregatable: true },
          { id: 6, metricId: 'EXPORT', name: 'Exports', definition: 'Total exports value', unit: 'USD', isAggregatable: true },
          { id: 7, metricId: 'IMPORT', name: 'Imports', definition: 'Total imports value', unit: 'USD', isAggregatable: true },
          { id: 8, metricId: 'DEBT', name: 'National Debt', definition: 'Government debt', unit: 'USD', isAggregatable: true },

          // Non-aggregatable metrics
          { id: 9, metricId: 'UNEMP', name: 'Unemployment Rate', definition: 'Unemployment Rate Percentage', unit: '%', isAggregatable: false },
          { id: 10, metricId: 'INFL', name: 'Inflation Rate', definition: 'Annual Inflation Rate', unit: '%', isAggregatable: false },
          { id: 11, metricId: 'INTEREST', name: 'Interest Rate', definition: 'Central bank interest rate', unit: '%', isAggregatable: false },
          { id: 12, metricId: 'GROWTH', name: 'GDP Growth Rate', definition: 'Annual GDP growth', unit: '%', isAggregatable: false },
          { id: 13, metricId: 'HDI', name: 'Human Development Index', definition: 'HDI Score', unit: 'Index', isAggregatable: false },
          { id: 14, metricId: 'GINI', name: 'Gini Coefficient', definition: 'Income inequality measure', unit: 'Index', isAggregatable: false },
          { id: 15, metricId: 'LITERACY', name: 'Literacy Rate', definition: 'Adult literacy rate', unit: '%', isAggregatable: false },
        ];

        // Set fallback data when API fails
        setCountries(fallbackCountries);
        setMetrics(fallbackMetrics);
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [onSubmitError]);

  const handleCountryChange = (value: (string | number)[]) => {
    setSelectedCountries(value as string[]);
  };

  const handleMetricChange = (value: (string | number)[]) => {
    setSelectedMetrics(value as number[]);
  };

  const handleYearChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCollectionYear(parseInt(event.target.value, 10));
  };

  const handleSubmit = async () => {
    if (selectedCountries.length === 0 || selectedMetrics.length === 0) {
      onSubmitError('Please select at least one country and one metric');
      return;
    }

    setLoading(true);
    try {
      const requests = [];
      for (const countryCode of selectedCountries) {
        const country = countries.find(c => c.iso_code === countryCode);
        for (const metricId of selectedMetrics) {
          const metric = metrics.find(m => m.id === metricId);
          requests.push({
            country_id: country?.id || 0,
            country_code: countryCode,
            country_name: country?.name || countryCode,
            metric_id: metricId,
            metric_name: metric?.name || '',
            metric_search_name: metric?.searchName || metric?.name || '',
            metric_description: metric?.definition || '',
            collection_year: collectionYear,
          });
        }
      }

      const response = await apiClient.post('/collection/requests', { requests });

      if (response.submitted_count) {
        onSubmitSuccess(response.submitted_count);
        // Clear selections after successful submission
        setSelectedCountries([]);
        setSelectedMetrics([]);
      }
    } catch (error: any) {
      console.error('Failed to submit collection requests:', error);
      let errorMessage = 'Failed to submit collection requests';

      if (error.response?.status === 404) {
        errorMessage = 'Collection service is not available. Please contact your administrator.';
      } else if (error.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      onSubmitError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getTotalCombinations = () => selectedCountries.length * selectedMetrics.length;

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress sx={{ color: theme.colors.primary.main }} />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontFamily: theme.fonts.primary,
            color: theme.colors.text.primary,
            fontWeight: 400
          }}
        >
          Submit Collection Requests
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.colors.text.secondary,
            fontFamily: theme.fonts.primary,
            mb: 3
          }}
        >
          Select countries and metrics to create collection requests. Each country-metric combination will generate a separate request.
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <MultiColumnSelect
          label="Countries"
          items={countries.map(country => ({
            value: country.iso_code,
            label: country.name,
            sublabel: country.iso_code,
            icon: country.flag_emoji || '',
            group: country.region || 'Other'
          }))}
          value={selectedCountries}
          onChange={handleCountryChange}
          columns={3}
          chipColor="primary"
          placeholder="Select countries"
          searchable={true}
          groupBy={true}
          selectAll={true}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <MultiColumnSelect
          label="Metrics"
          items={metrics.map(metric => ({
            value: metric.id,
            label: metric.name,
            sublabel: `${metric.metricId} - ${metric.unit}`,
            group: metric.isAggregatable ? 'Aggregatable' : 'Non-Aggregatable'
          }))}
          value={selectedMetrics}
          onChange={handleMetricChange}
          columns={2}
          chipColor="secondary"
          placeholder="Select metrics"
          searchable={true}
          groupBy={true}
          selectAll={true}
        />
      </Grid>

      <Grid item xs={12} md={4}>
        <TextField
          fullWidth
          type="number"
          label="Collection Year"
          value={collectionYear}
          onChange={handleYearChange}
          inputProps={{
            min: 1900,
            max: 2100,
          }}
          sx={{
            fontFamily: theme.fonts.primary,
            '& .MuiInputLabel-root': {
              fontFamily: theme.fonts.primary,
            },
            '& .MuiOutlinedInput-root': {
              fontFamily: theme.fonts.primary,
              '& fieldset': {
                borderColor: theme.colors.border.light,
              },
              '&:hover fieldset': {
                borderColor: theme.colors.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.colors.primary.main,
              },
            },
          }}
        />
      </Grid>

      {(selectedCountries.length > 0 || selectedMetrics.length > 0) && (
        <Grid item xs={12}>
          <Card
            variant="outlined"
            sx={{
              borderColor: theme.colors.border.light,
              borderRadius: theme.widget.borderRadius,
              boxShadow: theme.widget.boxShadow,
            }}
          >
            <CardContent>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  fontFamily: theme.fonts.primary,
                  color: theme.colors.text.primary,
                  fontWeight: 500
                }}
              >
                Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.colors.text.secondary,
                      fontFamily: theme.fonts.primary
                    }}
                  >
                    Selected Countries
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.primary.main
                    }}
                  >
                    {selectedCountries.length}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.colors.text.secondary,
                      fontFamily: theme.fonts.primary
                    }}
                  >
                    Selected Metrics
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.secondary.main
                    }}
                  >
                    {selectedMetrics.length}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.colors.text.secondary,
                      fontFamily: theme.fonts.primary
                    }}
                  >
                    Total Requests
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.status.success,
                      fontWeight: 600
                    }}
                  >
                    {getTotalCombinations()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setSelectedCountries([]);
              setSelectedMetrics([]);
            }}
            disabled={loading || (selectedCountries.length === 0 && selectedMetrics.length === 0)}
            sx={{
              fontFamily: theme.fonts.primary,
              borderColor: theme.colors.border.light,
              color: theme.colors.text.secondary,
              '&:hover': {
                borderColor: theme.colors.primary.main,
                backgroundColor: 'rgba(250, 175, 144, 0.05)',
              },
            }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <SendIcon />}
            onClick={handleSubmit}
            disabled={loading || selectedCountries.length === 0 || selectedMetrics.length === 0}
            sx={{
              fontFamily: theme.fonts.primary,
              backgroundColor: theme.colors.primary.main,
              color: '#fff',
              '&:hover': {
                backgroundColor: theme.colors.primary.dark,
              },
              '&:disabled': {
                backgroundColor: theme.colors.border.light,
              },
            }}
          >
            {loading ? 'Submitting...' : `Submit ${getTotalCombinations()} Request${getTotalCombinations() !== 1 ? 's' : ''}`}
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default CollectionForm;